/**
 * Level data schema, v2.1.0.
 *
 * This is one of the few things committed to *before* a second game exists,
 * because level JSON is authored data: changing it later means migrating every
 * file, not just editing code. See ADR-0010 and SDD-006-level-data-schema.md.
 *
 * The split is deliberate: everything a game does NOT need to interpret lives
 * in `Level`; everything game-specific lives in `Level.rules`, which the shell
 * treats as opaque. The shell can list, order, gate and score levels for a game
 * it knows nothing about.
 */

/** Bump on any breaking change. Loader rejects majors it does not understand.
 * 2.0.0 -> 2.1.0, sprint-8a stage 2, minor: `optionalGoals`, `avoid` and
 * `time` are additive, absent from every level that predates them, and
 * absence is legal (SDD-006 "Optional goal sets, added 2026-08-08"). */
export const LEVEL_SCHEMA_VERSION = '2.1.0';

export type GameId = string;
export type LevelId = string;

/**
 * `'daily'` was here and was removed in Sprint 1b. It needs a server, ADR-0007
 * rules that out for v1, and nothing authored or played one — but every rule
 * written in terms of the mode still had to say whether a daily level is ordered
 * (`DATA-012`) and whether its play is bounded (`DATA-013`), so its only effect
 * was to make two answers up. Add it back with the feature. See SDD-006 open
 * question 3.
 */
export type LevelMode = 'adventure' | 'freeplay';

/** Reach a score. Shell-interpretable. */
export interface ScoreGoal {
  kind: 'score';
  target: number;
}

/** Collect N of a named piece. `piece` is a game-defined token, e.g. "gem.red". */
export interface CollectGoal {
  kind: 'collect';
  piece: string;
  count: number;
}

/** Clear N obstacles of a game-defined kind. */
export interface ClearObstacleGoal {
  kind: 'clear-obstacle';
  obstacle: string;
  count: number;
}

/**
 * Escape hatch. A game may define goals the shell cannot reason about; the
 * shell renders `label` and relies on the game to report progress.
 */
export interface CustomGoal {
  kind: 'custom';
  id: string;
  label: string;
  target: number;
}

/**
 * Satisfied when the count of `piece` matched over the attempt does not
 * exceed `limit` -- the inverse of `CollectGoal`: a bar filling up is bad,
 * not good, and whatever renders it must say so (SDD-006). `limit: 0` is
 * legal and means "match none at all"; only negative is rejected
 * (`DATA-072`). Optional-goals-only in practice, never validated against
 * that here -- nothing about the shape of an `avoid` goal is invalid among
 * required goals, unlike `TimeGoal`.
 */
export interface AvoidGoal {
  kind: 'avoid';
  piece: string;
  limit: number;
}

/**
 * Satisfied when the attempt completes within `limitMs`. Optional goals
 * only -- required completion must never depend on speed (`A11Y-033`,
 * `A11Y-034`), so `DATA-073` rejects this kind among a level's required
 * `goals`. See SDD-006 for the full reasoning.
 */
export interface TimeGoal {
  kind: 'time';
  limitMs: number;
}

export type Goal = ScoreGoal | CollectGoal | ClearObstacleGoal | CustomGoal | AvoidGoal | TimeGoal;

export interface GridSize {
  cols: number;
  rows: number;
}

export interface Constraints {
  /** 0 means unlimited. */
  moveLimit: number;
  /** 0 means untimed. */
  timeLimitMs: number;
}

export interface Level {
  id: LevelId;
  gameId: GameId;
  schemaVersion: string;
  mode: LevelMode;
  title: string;
  /**
   * Ordering within the game's mode, 1-based. A mode with no ordering — a lone
   * freeplay level — uses 0. Unique within a mode, not across modes.
   */
  index: number;
  /** Author-facing hint, 1..10. Not used for gating. */
  difficulty: number;
  /** Deterministic RNG seed. Same seed + same rules => same board. */
  seed: number;
  grid: GridSize;
  constraints: Constraints;
  goals: Goal[];
  /**
   * Groups of goals that award the second and third stars without gating
   * completion (SDD-003 "Stars and completion", `PROG-042`). A separate
   * field rather than a flag on `Goal`: `outcome === 'won'` means every goal
   * in `goals` is complete (`M3-062`), so an optional member of that same
   * array would make an optional objective mandatory. At most two sets
   * (`DATA-069`), none empty (`DATA-070`). A set's identity is its content,
   * via `goalSetKey` (order-independent, target-sensitive), not its position
   * in this array -- reordering, inserting or removing a set in a shipped
   * level is safe (sprint-8b stage 1; a positional scheme, this field's own
   * shape until then, was not: `LevelRecord.completedGoalSets` used to store
   * indices into this array, so reordering it silently reassigned earned
   * stars). Absent, or an empty array, on a level worth exactly one star,
   * which is a legitimate level.
   */
  optionalGoals?: Goal[][];
  /**
   * Game-specific payload. The shell never reads inside this. Match-3 puts
   * `spawnTable` and the optional `initialBoard` here.
   */
  rules?: Record<string, unknown>;
}

/** Entry in a game's levels/index.json. Enough to render a level select
 *  without fetching every level file. */
export interface LevelIndexEntry {
  id: LevelId;
  index: number;
  title: string;
  difficulty: number;
  mode: LevelMode;
  file: string;
}

export interface LevelIndex {
  gameId: GameId;
  schemaVersion: string;
  levels: LevelIndexEntry[];
}

/**
 * `GameResult.goalProgress` and `GameProgress.goalProgress`
 * (`@contracts/game-module`) are keyed by "goal id", but `Goal` has no such
 * field except on `CustomGoal` -- the key has to include enough of the
 * goal's own data to tell two `collect` goals apart (a level can legitimately
 * define two, for two different pieces), not just its kind. Stable across a
 * save/resume round trip and across the game/shell boundary because it is
 * derived entirely from the goal's own public fields, never from array
 * position or anything game-specific.
 *
 * Lives here rather than in match-3's own `logic/goals.ts` (where it
 * originated, Sprint 3) because Sprint 5c stage 1 gave it a second consumer:
 * the shell's own HUD needs the identical mapping to match a game's live
 * `GameProgress.goalProgress` against `Level.goals` for display, and it can
 * only do that without importing a game's logic (this project's one
 * inviolable direction) if the function computing the key is itself a
 * `@contracts` citizen -- true already, since it only ever switches on
 * `Goal.kind`, a type this file already owns. Not a generalisation ahead of
 * need: the second consumer exists today, in this stage, not hypothetically.
 */
export function goalKey(goal: Goal): string {
  switch (goal.kind) {
    case 'score':
      return 'score';
    case 'collect':
      return `collect:${goal.piece}`;
    case 'clear-obstacle':
      return `clear-obstacle:${goal.obstacle}`;
    case 'custom':
      return goal.id;
    case 'avoid':
      return `avoid:${goal.piece}`;
    case 'time':
      return 'time';
  }
}

/**
 * `DATA-071`, sprint-8b stage 0. True when satisfying `required` necessarily
 * satisfies `optional` too -- same kind, same subject, and `required`'s own
 * target at least as demanding as `optional`'s. This is the decidable
 * replacement for the 2026-08-08 narrowing that used `goalKey` equality
 * alone: `goalKey` is deliberately target-blind (it keys `goalProgress`,
 * where "score" is one bucket regardless of the number), so that narrowing
 * rejected every same-kind optional goal outright, including a strictly
 * *harder* one -- "collect 40 red" when the level requires 28 is a
 * legitimate optional goal, not a vacuous one, and the old rule could not
 * tell the two apart. Subsumption can.
 *
 * `avoid` inverts the comparison on purpose: a *lower* limit is the
 * stricter one ("match at most 3" implies "match at most 5", not the other
 * way round), so it is `required.limit <= optional.limit`, the only arm
 * that reads backwards from its four siblings. `time` cannot occur as a
 * required goal (`DATA-073` rejects it outright), so this arm is dead code
 * in practice -- implemented anyway rather than a `switch` that lies about
 * being unreachable, on the same reasoning `avoid`'s own doc comment above
 * already gives for validating a shape nothing can currently construct.
 */
export function goalSubsumes(required: Goal, optional: Goal): boolean {
  switch (required.kind) {
    case 'score':
      return optional.kind === 'score' && required.target >= optional.target;
    case 'collect':
      return (
        optional.kind === 'collect' &&
        required.piece === optional.piece &&
        required.count >= optional.count
      );
    case 'clear-obstacle':
      return (
        optional.kind === 'clear-obstacle' &&
        required.obstacle === optional.obstacle &&
        required.count >= optional.count
      );
    case 'custom':
      return (
        optional.kind === 'custom' &&
        required.id === optional.id &&
        required.target >= optional.target
      );
    case 'avoid':
      return (
        optional.kind === 'avoid' &&
        required.piece === optional.piece &&
        required.limit <= optional.limit
      );
    case 'time':
      return optional.kind === 'time' && required.limitMs <= optional.limitMs;
  }
}

/**
 * One goal's own identity string -- unlike `goalKey`, which is deliberately
 * target-blind (it keys `goalProgress`, where "score" is one bucket
 * regardless of the number), this includes the distinguishing numeric value,
 * because two `score` goals with different targets are different objectives
 * for banking purposes even though they share a `goalProgress` bucket.
 * Private to this module: nothing outside `goalSetKey` needs a single goal's
 * identity on its own.
 */
function goalIdentity(goal: Goal): string {
  switch (goal.kind) {
    case 'score':
      return `score:${String(goal.target)}`;
    case 'collect':
      return `collect:${goal.piece}:${String(goal.count)}`;
    case 'clear-obstacle':
      return `clear-obstacle:${goal.obstacle}:${String(goal.count)}`;
    case 'custom':
      return `custom:${goal.id}:${String(goal.target)}`;
    case 'avoid':
      return `avoid:${goal.piece}:${String(goal.limit)}`;
    case 'time':
      return `time:${String(goal.limitMs)}`;
  }
}

/**
 * `PROG-042`/`043`, sprint-8b stage 1. Content-derived identity for one
 * optional goal set, order-independent within the set (goals sorted before
 * joining -- "a set is a set") and target-sensitive (via `goalIdentity`,
 * not `goalKey`, or this would inherit `goalKey`'s exact under-
 * differentiation problem). Replaces `LevelRecord.completedGoalSets`'s old
 * scheme of storing positional indices into `Level.optionalGoals` --
 * `optionalGoals`'s own doc comment used to warn that reordering that array
 * in a shipped level reassigns earned stars, a documented footgun that
 * stayed survivable only as long as nothing reordered the array. 8c's own
 * job is editing these arrays across eleven levels, so it stopped being
 * survivable.
 *
 * A plain string, not a `Set` object or a structured value, for the same
 * reason `completedGoalSets` is an array rather than a `Set` object
 * (SDD-003): it has to survive `JSON.parse`/`JSON.stringify` and
 * `SaveMigration`'s own `Record<string, unknown>` shape unchanged, and a
 * string is the one representation both boundaries pass through as the
 * identical value.
 *
 * Deliberately not built on `goalSubsumes`'s own per-kind comparisons, even
 * though both switch on goal kind: subsumption is a directional inequality
 * that reads backwards for `avoid` (`goalSubsumes`'s own doc comment), and
 * identity has no direction to get backwards -- forcing them through one
 * shared per-kind helper would trade a real difference in shape for an
 * indirection with no reuse underneath it.
 */
export function goalSetKey(set: readonly Goal[]): string {
  return set.map(goalIdentity).sort().join('|');
}
